import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedCashbook,
  useRestoreCashbook,
  usePermanentlyDeleteCashbook,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  BookOpen,
} from 'lucide-react';

const CURRENCY = '৳';
const LIMIT = 20;

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function typeClass(type: string): string {
  switch (type) {
    case 'opening':
      return 'bg-success/10 text-success';
    case 'closing':
      return 'bg-danger/10 text-danger';
    case 'refund':
      return 'bg-warning/10 text-warning';
    default:
      return 'bg-primary/10 text-primary';
  }
}

export function BillingCashbookTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; entry?: any } | null>(
    null,
  );
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedCashbook(page, LIMIT, debouncedSearch.trim() || undefined);
  const restoreCashbook = useRestoreCashbook();
  const permanentDelete = usePermanentlyDeleteCashbook();

  const entries = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const allSelected = entries.length > 0 && entries.every((e: any) => selectedIds.includes(e.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !entries.some((e: any) => e.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...entries.map((e: any) => e.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      await restoreCashbook.mutateAsync([id]);
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Entry restored');
    } catch {
      toast.error('Failed to restore entry');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restoreCashbook.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? selectedIds.length;
      toast.success(`Restored ${restored} entr${restored === 1 ? 'y' : 'ies'}`);
      setSelectedIds([]);
    } catch {
      toast.error('Failed to restore some entries');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        await permanentDelete.mutateAsync([confirmTarget.entry.id]);
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.entry.id));
        toast.success('Entry permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? selectedIds.length;
        toast.success(`Permanently deleted ${deleted} entr${deleted === 1 ? 'y' : 'ies'}`);
        setSelectedIds([]);
      }
    } catch {
      if (confirmTarget.mode === 'bulk') toast.error('Failed to delete some entries');
      else toast.error('Failed to delete entry');
    } finally {
      setConfirmTarget(null);
    }
  };

  const busy = restoreCashbook.isPending || permanentDelete.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/billing/cashbook')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Cashbook Trash</h1>
            <p className="text-xs text-text-muted">Restore entries or delete them permanently</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trashed entries by description..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restoreCashbook.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed cashbook entries</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={entries.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Shift</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Debit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Credit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Balance</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-16 text-center">
                      <BookOpen className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed entries match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  entries.map((entry: any) => (
                    <tr key={entry.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(entry.id)}
                          onChange={() => toggleSelectOne(entry.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-text">
                        {new Date(entry.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${typeClass(entry.type)}`}>
                          {entry.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text max-w-xs truncate" title={entry.entryDescription}>
                        {entry.entryDescription}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-muted">{entry.shiftCode || '—'}</td>
                      <td className="px-4 py-3 text-sm text-success text-right">
                        {entry.debit ? fmt(entry.debit) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-danger text-right">
                        {entry.credit ? fmt(entry.credit) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-text text-right">
                        {fmt(entry.runningBalance)}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {entry.deletedAt ? new Date(entry.deletedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRestore(entry.id)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Restore"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', entry })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed entries"
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete entries permanently' : 'Delete entry permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected entry(ies)? This cannot be undone.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.entry?.entryDescription}</strong>? This cannot be
              undone.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
